import React, { useState, useEffect } from "react";
import { Modal, Descriptions, Button, Tag, Avatar, Tabs, Table, message } from "antd";
import moment from "moment";
import { platformApi } from "../../../../api";
import { ENDPOINT } from "../../../../constants";
import BookingRegisterModal from "../../BookingRegister/BookingRegisterModal";
import JobOrderModal from "../../JobOrder/JobOrderModal";

const { TabPane } = Tabs;

const LedgerModal = ({ visible, onCancel, data, type, initialSubTab }) => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [subTab, setSubTab] = useState(initialSubTab || "all");
  const [overallBalance, setOverallBalance] = useState(0);
  const [bookingModal, setBookingModal] = useState(false);
  const [jobModal, setJobModal] = useState(false);
  const [dataSource, setDataSource] = useState({});

  // Denomination Modal State
  const [denoModalVisible, setDenoModalVisible] = useState(false);
  const [selectedDenominations, setSelectedDenominations] = useState([]);

  useEffect(() => {
    const typesWithHistory = ["BANK_ACCOUNT", "CASH_ACCOUNT", "CUSTOMERS", "EMPLOYEES", "PINELABS_ACCOUNT"];
    if (visible && typesWithHistory.includes(type)) {
      fetchTransactions();
    }
  }, [visible, type, subTab, currentPage, data?.id]);

  useEffect(() => {
    if (visible) {
      setCurrentPage(1);
      setSubTab(initialSubTab || "all");
      setOverallBalance(data?.overallBalance || 0);
    }
  }, [visible, initialSubTab]);

  const handleSubTabChange = (key) => {
    setSubTab(key);
    setCurrentPage(1);
  };

  const handleModuleClick = async (data) => {
    if (data.module == "BOOKING_REGISTER" || data.module == "BOOKING_PAYMENT" || data.module == "BOOKING") {
      const success = await getBookingData(data.moduleId);
      if (success) {
        setBookingModal(true);
      }
    } else if (data.module == "JOB_INVOICE") {
      window.open(`/autoadmin/job_invoice/${data.moduleId}`, "_blank");
    } else if (data.module == "COUNTER_SALES") {
      window.open(`/autoadmin/counter_sale/${data.moduleId}`, "_blank");
    } else if (data.module == "JobOrder") {
      getJobOrder(data.moduleId);
    } else if (data.module == "CSO") {
      window.open(`/autoadmin/customerspare/${data.moduleId}`, "_blank");
    }
  }

  const getBookingData = async (id) => {
    try {
      const res = await platformApi.get(`/api/booking/${id}`);
      if (res.data.code === 200) {
        const responseData = res.data.response?.code === 200 ? res.data.response.data : (res.data.response || res.data.data);
        setDataSource(responseData);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error fetching booking data:", err);
      return false;
    }
  }

  const getJobOrder = (id) => {
    platformApi
      .get(`/api/jobOrder/${id}`)
      .then((res) => {
        let { data } = res;
        if (data.code === 200 && data.response.code === 200) {
          setDataSource(data.response.data);
          setJobModal(true);
        } else {
          message.error("Unable to fetch job order details", 2);
        }
      })
      .catch(() => {
        message.error("Error while fetching job order", 2);
      });
  };

  const fetchTransactions = async () => {
    if (!data?.id) return;
    setLoading(true);
    try {
      let url = "";
      let payload = {
        page: currentPage,
        size: 10,
      };

      if (type === "BANK_ACCOUNT") {
        url = `/api/ledger/bankAccount/${data.id}`;
        payload.module = subTab;
      } else if (type === "CASH_ACCOUNT") {
        url = `/api/ledger/cashAccount/${data.id}`;
        payload.status = subTab; // Controller uses 'status' for cash
      } else if (type === "CUSTOMERS") {
        url = `/api/ledger/customer/${data.id}`;
        payload.module = subTab === "all" ? "" : subTab;
      } else if (type === "EMPLOYEES") {
        url = `/api/ledger/employee/${data.profile?.id}`;
        payload.module = subTab;
      } else if (type === "PINELABS_ACCOUNT") {
        url = `/api/ledger/pinelabs/${data.id}`;
        payload.mode = subTab;
      }

      const res = await platformApi.post(url, payload);
      if (res.data.code === 200) {
        setTransactions(res.data.data.transactions || []);
        setTotal(res.data.data.count || 0);
        setOverallBalance(res.data.data.overallBalance || 0);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      message.error("Failed to fetch transaction history");
    } finally {
      setLoading(false);
    }
  };

  const bankColumns = [
    {
      title: "Id",
      key: "ref",
      render: (record) => <div>{record.receiptId || "-"}</div>,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render: (date) => moment(date).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: "Type",
      key: "type",
      render: (record) => (
        <Tag color={record.transactionType === "payment" ? "red" : "green"}>
          {record?.transactionType?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Module",
      key: "ref",
      render: (record) => {
        const formattedModule = record.module
          ?.replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase());
        return <span className="linkylink" onClick={() => handleModuleClick(record)}>{formattedModule == "Cso" ? "Customer Spare Order" : formattedModule || "-"}</span>;
      },
    },
    {
      title: "Amount(₹)",
      key: "amount",
      render: (record) => <div>{record.collectedAmount}</div>,
    },
    {
      title: "Balance(₹)",
      dataIndex: "accountBalance",
      key: "balance",
    },
    {
      title: "Paid By",
      key: "paidBy",
      render: (record) => {
        const person = record.paidBy;
        if (!person) return "-";
        if (typeof person === "object") {
          return person.name || person.employeeName || "-";
        }
        return person;
      },
    },
    ...(subTab === "receipt" || subTab === "all"
      ? [
        {
          title: "Collected By",
          key: "collectedBy",
          render: (record) => record.collectedBy?.employeeName || "-",
        },
      ]
      : []),
    ...(subTab === "payment"
      ? [
        {
          title: "Payment Type",
          key: "paymentType",
          render: (record) => {
            const formattedType = record.paymentType
              ?.replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase());

            return formattedType || "-";
          },
        },
      ]
      : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag>{status}</Tag>,
    },
  ];


  const cashColumns = [
    {
      title: "Id",
      key: "ref",
      render: (record) => <div>{record.receiptId || "-"}</div>,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render: (date) => moment(date).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: "Type",
      key: "type",
      render: (record) => (
        <Tag color={record.transactionType !== "collected" ? "red" : "green"}>
          {record?.transactionType?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Module",
      key: "ref",
      render: (record) => {
        const formattedModule = record.module
          ?.replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase());
        return <span className="linkylink" onClick={() => handleModuleClick(record)}>{formattedModule == "Cso" ? "Customer Spare Order" : formattedModule || "-"}</span>;
      },
    },
    {
      title: "Amount(₹)",
      key: "amount",
      render: (record) => record.collectedAmount || record.totalAmount || 0,
    },
    {
      title: "Balance(₹)",
      dataIndex: "accountBalance",
      key: "balance",
    },
    ...(subTab === "collected" || subTab === "all"
      ? [
        {
          title: "Paid By",
          key: "paidBy",
          render: (record) => record.paidBy?.name || "-",
        },
      ]
      : []),
    {
      title:
        subTab === "all"
          ? "Handled By"
          : subTab === "collected"
            ? "Collected By"
            : "Withdrawn By",
      key: "by",
      render: (record) => {
        if (record.transactionType === "collected") {
          const by = record.collectedBy?.employeeName;
          return <div>{by || "-"}</div>;
        } else {
          const by = record.withdrawnBy?.profile?.employeeName;
          return <div>{by || "-"}</div>;
        }
      },
    },
    {
      title: "Denominations",
      key: "denominations",
      render: (record) => (
        <span
          className="linkylink"
          onClick={() => {
            setSelectedDenominations(record.denominations || []);
            setDenoModalVisible(true);
          }}
        >
          View
        </span>
      ),
    },
  ];

  const customerColumns = [
    {
      title: "Id",
      key: "ref",
      render: (record) => <div>{record.receiptId || "-"}</div>,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render: (date) => moment(date).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      render: (method) => {
        const formattedModule = method
          ?.replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase());
        return <div>{formattedModule || "-"}</div>;
      }
    },
    {
      title: "Module",
      key: "ref",
      render: (record) => {
        const formattedModule = record.module
          ?.replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase());
        return <span className="linkylink" onClick={() => handleModuleClick(record)}>{formattedModule == "Cso" ? "Customer Spare Order" : formattedModule || "-"}</span>;
      },
    },
    {
      title: "Amount(₹)",
      key: "amount",
      render: (record) => <div>{record.collectedAmount}</div>,
    },
    {
      title: "Balance(₹)",
      dataIndex: "accountBalance",
      key: "balance",
    },
    {
      title: "Paid By",
      key: "paidBy",
      render: (record) => {
        const person = record.paidBy;
        if (!person) return "-";
        if (typeof person === "object") {
          return person.name || person.employeeName || "-";
        }
        return person;
      },
    },
    ...(subTab === "receipt" || subTab === "all"
      ? [
        {
          title: "Collected By",
          key: "collectedBy",
          render: (record) => record.collectedBy?.employeeName || "-",
        },
      ]
      : []),
    ...(subTab === "payment"
      ? [
        {
          title: "Payment Type",
          key: "paymentType",
          render: (record) => {
            const formattedType = record.paymentType
              ?.replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase());

            return formattedType || "-";
          },
        },
      ]
      : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag>{status}</Tag>,
    },
    {
      title: "Denominations",
      key: "denominations",
      render: (record) => record.denominations?.length > 0 ? <span className="linkylink" onClick={() => {
        setSelectedDenominations(record.denominations || []);
        setDenoModalVisible(true);
      }}>View</span> : "-"
    },
  ];

  const employeeColumns = [
    {
      title: "Id",
      key: "ref",
      render: (record) => <div>{record.receiptId || "-"}</div>,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render: (date) => moment(date).format("DD-MM-YYYY HH:mm"),
    },
    ...(subTab == "all" || subTab == "receipt"
      ? [{
        title: "Method",
        dataIndex: "method",
        key: "method",
        render: (method) => {
          const formattedMethod = method
            ?.replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
          return <div>{formattedMethod || "-"}</div>;
        }
      }] : []),
    ...(subTab === "all"
      ? [
        {
          title: "Type",
          dataIndex: "transactionType",
          key: "type",
          render: (type) => (
            <Tag color={type === "receipt" ? "green" : "volcano"}>
              {type?.toUpperCase()}
            </Tag>
          ),
        },
      ]
      : []),
    {
      title: "Module",
      key: "ref",
      render: (record) => {
        const formattedModule = record.module
          ?.replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase());
        return <span className="linkylink" onClick={() => handleModuleClick(record)}>{formattedModule == "Cso" ? "Customer Spare Order" : formattedModule || "-"}</span>;
      },
    },
    {
      title: "Amount(₹)",
      key: "amount",
      render: (record) => <div>{record.collectedAmount}</div>,
    },
    {
      title: "Balance(₹)",
      dataIndex: "accountBalance",
      key: "balance",
    },
    {
      title: "Paid By",
      key: "paidBy",
      render: (record) => {
        const person = record.paidBy;
        if (!person) return "-";
        if (typeof person === "object") {
          return person.name || person.employeeName || "-";
        }
        return person;
      },
    },
    ...(subTab === "receipt" || subTab === "all"
      ? [
        {
          title: "Collected By",
          key: "collectedBy",
          render: (record) => record.collectedBy?.employeeName || "-",
        },
      ]
      : []),
    ...(subTab === "payment"
      ? [
        {
          title: "Payment Type",
          key: "paymentType",
          render: (record) => {
            const formattedType = record.paymentType
              ?.replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase());

            return formattedType || "-";
          },
        },
      ]
      : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag>{status}</Tag>,
    },
    ...((subTab == "all" || subTab == "receipt") ? [{
      title: "Denominations",
      key: "denominations",
      render: (record) => record.denominations?.length > 0 ? <span className="linkylink" onClick={() => {
        setSelectedDenominations(record.denominations || []);
        setDenoModalVisible(true);
      }}>View</span> : "-"
    }] : [])
  ];

  const pinelabsColumns = [
    {
      title: "Id",
      key: "ref",
      render: (record) => <div>{record.receiptId || "-"}</div>,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render: (date) => moment(date).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
    },
    {
      title: "Module",
      key: "ref",
      render: (record) => {
        const formattedModule = record.module
          ?.replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase());
        return <span className="linkylink" onClick={() => handleModuleClick(record)}>{formattedModule == "Cso" ? "Customer Spare Order" : formattedModule || "-"}</span>;
      },
    },
    {
      title: "Amount(₹)",
      key: "amount",
      render: (record) => <div>{record.collectedAmount}</div>,
    },
    {
      title: "Balance(₹)",
      dataIndex: "accountBalance",
      key: "balance",
    },
    {
      title: "Paid By",
      key: "paidBy",
      render: (record) => {
        const person = record.paidBy;
        if (!person) return "-";
        if (typeof person === "object") {
          return person.name || person.employeeName || "-";
        }
        return person;
      },
    },
    {
      title: "Collected By",
      key: "collectedBy",
      render: (record) => record.collectedBy?.employeeName || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag>{status}</Tag>,
    }
  ];

  const renderDetails = () => {
    if (!data) return null;

    switch (type) {
      case "BANK_ACCOUNT":
        return (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Bank Name">{data.name}</Descriptions.Item>
              <Descriptions.Item label="Account Number">{data.accountNumber}</Descriptions.Item>
              <Descriptions.Item label="Account Name">{data.accountName}</Descriptions.Item>
              <Descriptions.Item label="IFSC Code">{data.ifsc}</Descriptions.Item>
              <Descriptions.Item label="Account Type">{data.accountType}</Descriptions.Item>
              <Descriptions.Item label="Branch">{data.branchName}</Descriptions.Item>
              <Descriptions.Item label="Overall Balance (₹)">{overallBalance}</Descriptions.Item>
            </Descriptions>
            <Tabs activeKey={subTab} onChange={handleSubTabChange} size="small">
              <TabPane tab="All" key="all" />
              <TabPane tab="Receipt" key="receipt" />
              <TabPane tab="Payment" key="payment" />
            </Tabs>
            <Table
              loading={loading}
              columns={bankColumns}
              dataSource={transactions}
              rowKey="id"
              pagination={{
                current: currentPage,
                total: total,
                pageSize: 10,
                onChange: (page) => setCurrentPage(page),
              }}
            />
          </>
        );
      case "CASH_ACCOUNT":
        return (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Branch Name">{data.name}</Descriptions.Item>
              <Descriptions.Item label="Account Balance">{overallBalance}</Descriptions.Item>
            </Descriptions>
            <Tabs activeKey={subTab} onChange={handleSubTabChange} size="small">
              <TabPane tab="All" key="all" />
              <TabPane tab="Collected" key="collected" />
              <TabPane tab="Withdrawn" key="withdrawal" />
            </Tabs>
            <Table
              loading={loading}
              columns={cashColumns}
              dataSource={transactions}
              rowKey="id"
              pagination={{
                current: currentPage,
                total: total,
                pageSize: 10,
                onChange: (page) => setCurrentPage(page),
              }}
            />
          </>
        );
      case "CUSTOMERS":
        return (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Name">{data.name}</Descriptions.Item>
              <Descriptions.Item label="Email">{data.email}</Descriptions.Item>
              <Descriptions.Item label="Mobile No">
                {(data.contacts || [])
                  .map((c) => c?.phone)
                  .filter(Boolean)
                  .join(" / ") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Type">{data.customerType}</Descriptions.Item>
              <Descriptions.Item label="DOB">{data.dateOfBirth ? moment(data.dateOfBirth).format("DD-MM-YYYY") : "-"}</Descriptions.Item>
              <Descriptions.Item label="Locality">{data.address ? data.address.locality : "-"}</Descriptions.Item>
            </Descriptions>
            <Tabs activeKey={subTab} onChange={handleSubTabChange} size="small">
              <TabPane tab="All" key="all" />
              <TabPane tab="Booking Register" key="BOOKING_REGISTER" />
              <TabPane tab="Job Invoice" key="JOB_INVOICE" />
              <TabPane tab="Job Order" key="JobOrder" />
              <TabPane tab="Spare Order" key="CSO" />
              <TabPane tab="Counter Sales" key="COUNTER_SALES" />
            </Tabs>
            <Table
              loading={loading}
              columns={customerColumns} // Using same columns as Bank for now as it's payment history
              dataSource={transactions}
              rowKey="id"
              pagination={{
                current: currentPage,
                total: total,
                pageSize: 10,
                onChange: (page) => setCurrentPage(page),
              }}
            />
          </>
        );
      case "EMPLOYEES":
        return (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Name" span={2}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Avatar src={data.profilePicture} icon={!data.profilePicture && "user"} />
                  {data.profile ? data.profile.employeeName : "-"}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Phone">{data.phone}</Descriptions.Item>
              <Descriptions.Item label="Phone 2">{data.phone2}</Descriptions.Item>
              <Descriptions.Item label="Joined On">{data.profile ? moment(data.profile.dateOfJoining).format("DD-MM-YYYY") : "-"}</Descriptions.Item>
              <Descriptions.Item label="Department">{data.profile && data.profile.department ? data.profile.department.role : "-"}</Descriptions.Item>
            </Descriptions>
            <Tabs activeKey={subTab} onChange={handleSubTabChange} size="small">
              <TabPane tab="All" key="all" />
              <TabPane tab="Receipt" key="receipt" />
              <TabPane tab="Payment" key="payment" />
            </Tabs>
            <Table
              loading={loading}
              columns={employeeColumns}
              dataSource={transactions}
              rowKey="id"
              pagination={{
                current: currentPage,
                total: total,
                pageSize: 10,
                onChange: (page) => setCurrentPage(page),
              }}
            />
          </>
        );
      case "PINELABS_ACCOUNT":
        return (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Machine ID">{data.machineId}</Descriptions.Item>
              <Descriptions.Item label="Branch">{data.branch?.name}</Descriptions.Item>
              <Descriptions.Item label="Client ID">{data.clientId}</Descriptions.Item>
              <Descriptions.Item label="Merchant ID">{data.merchantId}</Descriptions.Item>
              <Descriptions.Item label="Store ID">{data.storeId}</Descriptions.Item>
              <Descriptions.Item label="Overall Balance (₹)">{overallBalance}</Descriptions.Item>
            </Descriptions>
            <Tabs activeKey={subTab} onChange={handleSubTabChange} size="small">
              <TabPane tab="All" key="all" />
              <TabPane tab="UPI" key="UPI" />
              <TabPane tab="CARD" key="CARD" />
            </Tabs>
            <Table
              loading={loading}
              columns={pinelabsColumns}
              dataSource={transactions}
              rowKey="id"
              pagination={{
                current: currentPage,
                total: total,
                pageSize: 10,
                onChange: (page) => setCurrentPage(page),
              }}
            />
          </>
        );
      case "SUPPLIERS":
        return (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Name">{data.name}</Descriptions.Item>
            <Descriptions.Item label="Status">{data.status}</Descriptions.Item>
            <Descriptions.Item label="GSTIN">{data.GSTIN}</Descriptions.Item>
            <Descriptions.Item label="Dealer Type">{data.dealerType}</Descriptions.Item>
            <Descriptions.Item label="City">{data.address && data.address.district ? data.address.district.name : "-"}</Descriptions.Item>
          </Descriptions>
        );
      default:
        return <pre>{JSON.stringify(data, null, 2)}</pre>;
    }
  };

  const denoColumns = [
    { title: "Denomination", dataIndex: "denomination", key: "denomination" },
    { title: "Count", dataIndex: "count", key: "count" },
    { title: "Amount", dataIndex: "amount", key: "amount" },
  ];

  return (
    <>
      <Modal
        title={`${type?.replace(/_/g, " ")?.toLowerCase()?.replace(/^./, (char) => char.toUpperCase())} Ledger Details`}
        visible={visible}
        onCancel={onCancel}
        footer={[
          <Button key="close" onClick={onCancel}>
            Close
          </Button>,
        ]}
        width={"85%"}
      >
        {renderDetails()}
      </Modal>

      <Modal
        title="Denomination Details"
        visible={denoModalVisible}
        onCancel={() => setDenoModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDenoModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        <Table
          columns={denoColumns}
          dataSource={selectedDenominations}
          rowKey="id"
          pagination={false}
        />
      </Modal>

      {
        bookingModal &&
        <BookingRegisterModal
          open={bookingModal}
          close={() => {
            setBookingModal(false);
          }}
          mode={dataSource?.bookingStatus === "ADVANCE" ? "AdvancedBooking" : "ConfirmBooking"}
          currentData={dataSource}
          isSales={false}
          editable={false}
          bookingId={dataSource?.bookingId}
          clearBookingData={() => {
            setDataSource({});
          }}
        />
      }

      {jobModal && (
        <JobOrderModal
          open={jobModal}
          close={() => {
            setJobModal(false);
            setDataSource({});
          }}
          data={dataSource}
          isFromCustomer={true}
        />
      )}
    </>
  );
};

export default LedgerModal;
